import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";
import { Stack, Box, Divider } from "@mui/material";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "wrap"
  },
  btnWrapper: {
    position: "relative"
  },
  optionRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  }
}));

const FlowBuilderRandomizerModal = ({
  open,
  onSave,
  data,
  onUpdate,
  close
}) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const [options, setOptions] = useState([{ percent: 50 }, { percent: 50 }]);
  const [activeModal, setActiveModal] = useState(false);

  const [name, setName] = useState("");

  useEffect(() => {
    if (open === "edit") {
      setName(data.data.name || "");
      if (data.data.percents && data.data.percents.length > 0) {
        setOptions(data.data.percents.map(p => ({ percent: (p !== null && p !== undefined && !isNaN(p)) ? p : (100 / (data.data.percents.length || 2)) })));
      } else if (data.data.percent) {
        setOptions([{ percent: data.data.percent }, { percent: 100 - data.data.percent }]);
      } else {
        setOptions([{ percent: 50 }, { percent: 50 }]);
      }
      setActiveModal(true);
    } else if (open === "create") {
      setName("");
      setOptions([{ percent: 50 }, { percent: 50 }]);
      setActiveModal(true);
    }
    return () => {
      isMounted.current = false;
    };
  }, [open, data]);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const recalculatePercents = (count) => {
    const base = Math.floor(100 / count);
    const remainder = 100 % count;
    const newPercents = Array(count).fill(base);

    // Distribute remainder to first few items
    for (let i = 0; i < remainder; i++) {
      newPercents[i]++;
    }

    return newPercents.map(p => ({ percent: p }));
  };

  const handleAddOption = () => {
    if (options.length < 4) {
      const newCount = options.length + 1;
      setOptions(recalculatePercents(newCount));
    }
  };

  const handleRemoveOption = (index) => {
    if (options.length > 2) {
      const newCount = options.length - 1;
      setOptions(recalculatePercents(newCount));
    }
  };

  // Removed manual change handler or made it ineffective
  const handlePercentChange = (index, value) => {
    // Disabled for Round Robin
  };

  const handleSave = () => {
    const total = options.reduce((sum, opt) => sum + opt.percent, 0);
    if (total !== 100) {
      return toast.error(i18n.t("flowBuilder.randomizer.errorSum"));
    }

    const percents = options.map(opt => opt.percent);

    if (open === "edit") {
      onUpdate({
        ...data,
        data: {
          ...data.data,
          name: name,
          percents: percents,
          percent: percents[0] // Fallback for old compatibility if needed
        }
      });
    } else if (open === "create") {
      onSave({
        name: name,
        percents: percents,
        percent: percents[0]
      });
    }
    handleClose();
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={activeModal}
        onClose={handleClose}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {open === "create"
            ? i18n.t("flowBuilder.randomizer.addTitle")
            : i18n.t("flowBuilder.randomizer.editTitle")}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ p: 2 }}>
            <TextField
              label={i18n.t("flowBuilder.form.name")}
              variant="outlined"
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
              margin="dense"
              className={classes.textField}
            />
          </Box>
          <Typography variant="body2" color="textSecondary" gutterBottom style={{ padding: '0 16px' }}>
            {i18n.t("flowBuilder.randomizer.description")}
          </Typography>
          <Box sx={{ mt: 2, p: 2 }}>
            {options.map((opt, index) => (
              <Box key={index} className={classes.optionRow}>
                <Typography sx={{ width: 100, fontWeight: 'bold' }}>
                  {i18n.t("flowBuilder.randomizer.pathName")} {String.fromCharCode(65 + index)}:
                </Typography>
                <TextField
                  type="number"
                  variant="outlined"
                  size="small"
                  value={opt.percent}
                  // onChange={(e) => handlePercentChange(index, e.target.value)}
                  InputProps={{
                    inputProps: { min: 0, max: 100 },
                    readOnly: true,
                    style: { opacity: 0.8 }
                  }}
                  suffix="%"
                  sx={{ flex: 1 }}
                />
                <Typography>%</Typography>
                {options.length > 2 && (
                  <IconButton color="secondary" onClick={() => handleRemoveOption(index)}>
                    <DeleteOutlineIcon />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>
          {options.length < 4 && (
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddOption}
              fullWidth
              variant="outlined"
              color="primary"
              sx={{ mt: 1 }}
            >
              {i18n.t("flowBuilder.randomizer.addPath")}
            </Button>
          )}
          <Box sx={{ mt: 2, textAlign: 'right' }}>
            <Typography variant="caption" color={options.reduce((s, o) => s + o.percent, 0) === 100 ? "primary" : "error"}>
              {i18n.t("flowBuilder.randomizer.total")}: {options.reduce((sum, opt) => sum + opt.percent, 0)}%
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="outlined" style={{ borderColor: "#ff4d4f", color: "#ff4d4f", textTransform: "none", fontWeight: "bold", borderRadius: "8px" }}>{i18n.t("flowBuilder.randomizer.cancel")}</Button>
          <Button  onClick={handleSave}  variant="contained" style={{ backgroundColor: "#7367F0", color: "#fff", textTransform: "none", fontWeight: "bold", borderRadius: "8px", boxShadow: "0 4px 14px 0 rgba(115, 103, 240, 0.39)" }}>{i18n.t("flowBuilder.randomizer.save")}</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FlowBuilderRandomizerModal;
